import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { ArrowRight, MapPin, Phone, Mail, CheckCircle2, Users, GraduationCap, CalendarDays, BookOpen } from "lucide-react";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const NAV = [["about", "About"], ["facilities", "Facilities"], ["gallery", "Gallery"], ["fees", "Fee Structure"], ["contact", "Contact"]];

export function useSchool(code) {
  return useQuery({ queryKey: ["pub-school", code], queryFn: async () => (await axios.get(`${API}/public/schools/${code}`)).data, retry: false });
}

export default function SchoolSite() {
  const { code } = useParams();
  const { data: s, isLoading, isError } = useSchool(code);

  if (isLoading) return <div className="mx-auto max-w-7xl px-5 py-24 text-slate-500">Loading school…</div>;
  if (isError || !s) return <div className="mx-auto max-w-7xl px-5 py-24 text-center"><h2 className="text-2xl font-head font-semibold">School not found</h2><Link to="/" className="pub-btn mt-6">Back to directory</Link></div>;
  const p = s.profile;

  return (
    <div data-testid="school-site">
      {/* School sub-nav */}
      <div className="sticky top-[61px] z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            {s.logo_url ? <img src={fileUrl(s.logo_url)} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white text-xs font-bold">{s.code.slice(0, 2)}</div>}
            <p className="truncate font-semibold font-head" data-testid="school-site-name">{s.name}</p>
          </div>
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map(([id, l]) => <a key={id} href={`#${id}`} className="rounded-full px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 hover:text-indigo-700">{l}</a>)}
          </nav>
          <div className="flex items-center gap-2">
            <Link to={`/s/${s.code}/fees`} className="pub-btn pub-btn-outline !py-2 !px-4 text-xs sm:text-sm" data-testid="pay-fees-btn">Pay Fees</Link>
            {p.admission_open && <Link to={`/s/${s.code}/admission`} className="pub-btn !py-2 !px-4 text-xs sm:text-sm" data-testid="apply-now-btn">Apply Now <ArrowRight size={15} /></Link>}
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={fileUrl(p.hero_image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/20" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-12 text-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">{p.board} · {p.medium}</span>
            <h1 className="mt-4 max-w-3xl text-4xl font-bold font-head tracking-tight sm:text-5xl lg:text-6xl">{s.name}</h1>
            <p className="mt-3 max-w-xl text-base text-slate-200 sm:text-lg">{p.tagline}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {p.admission_open && <Link to={`/s/${s.code}/admission`} className="pub-btn !bg-indigo-600 hover:!bg-indigo-500" data-testid="hero-apply-btn">Online Admission <ArrowRight size={16} /></Link>}
              <Link to={`/s/${s.code}/fees`} className="pub-btn !bg-white/15 !text-white backdrop-blur hover:!bg-white/25" data-testid="hero-pay-btn">Pay Fees Online</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats + About */}
      <section id="about" className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">About the School</p>
          <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">A place where every child is known</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{p.about}</p>
          {p.principal && p.principal !== "Principal" && (
            <p className="mt-6 text-sm text-slate-500">Principal · <span className="font-semibold text-slate-800">{p.principal}</span></p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 lg:col-span-5">
          {[[Users, s.students, "Students"], [GraduationCap, s.teachers, "Teachers"], [CalendarDays, p.established, "Established"], [BookOpen, s.classes?.length || 10, "Classes"]].map(([I, v, l]) => (
            <div key={l} className="pub-card p-5">
              <I size={20} className="text-indigo-600" />
              <p className="mt-3 text-3xl font-bold font-head">{v}</p>
              <p className="text-xs uppercase tracking-wider text-slate-500">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Facilities */}
      <section id="facilities" className="bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 py-16">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Facilities</p>
          <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">Everything a growing mind needs</h2>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="facilities-grid">
            {p.facilities.map((f) => (
              <div key={f} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium">
                <CheckCircle2 size={18} className="shrink-0 text-emerald-500" /> {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="mx-auto max-w-7xl px-5 py-16">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Gallery</p>
        <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">Life at {s.name.split(" ")[0]}</h2>
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="gallery-grid">
          {p.gallery.map((g, i) => (
            <motion.div key={g + i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className={`overflow-hidden rounded-2xl ${i % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
              <img src={fileUrl(g)} alt="" className="h-full w-full object-cover aspect-square transition duration-500 hover:scale-105" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Fee structure */}
      <section id="fees" className="bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-5 py-16">
          <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Fee Structure 2026-27</p>
          <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">Transparent, all-inclusive fees</h2>
          <div className="mt-8 overflow-hidden rounded-2xl border border-slate-800">
            <table className="w-full text-sm" data-testid="fee-structure-table">
              <thead className="bg-slate-800/60 text-left text-xs uppercase tracking-wider text-slate-400">
                <tr><th className="px-5 py-3">Class</th><th className="px-5 py-3 text-right">Tuition</th><th className="px-5 py-3 text-right">Books</th><th className="px-5 py-3 text-right">Transport (opt.)</th><th className="px-5 py-3 text-right">Total</th></tr>
              </thead>
              <tbody>
                {p.fee_structure.map((r) => (
                  <tr key={r.class_name} className="border-t border-slate-800">
                    <td className="px-5 py-3 font-medium">{r.class_name}</td>
                    <td className="px-5 py-3 text-right text-slate-300">{inr(r.tuition)}</td>
                    <td className="px-5 py-3 text-right text-slate-300">{inr(r.books)}</td>
                    <td className="px-5 py-3 text-right text-slate-300">{inr(r.transport)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-emerald-400">{inr((r.tuition || 0) + (r.books || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="mx-auto grid max-w-7xl gap-8 px-5 py-16 lg:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Contact</p>
          <h2 className="mt-1 text-2xl font-semibold font-head sm:text-3xl">Visit or call us</h2>
          <ul className="mt-6 space-y-4 text-sm">
            <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-indigo-600" /><span>{s.address}{s.city ? `, ${s.city}` : ""}</span></li>
            <li className="flex items-center gap-3"><Phone size={18} className="text-indigo-600" /><a href={`tel:${s.phone}`} className="hover:text-indigo-700">{s.phone}</a></li>
            <li className="flex items-center gap-3"><Mail size={18} className="text-indigo-600" /><a href={`mailto:${s.email}`} className="hover:text-indigo-700">{s.email}</a></li>
          </ul>
        </div>
        <div className="pub-card p-6">
          <h3 className="font-semibold font-head text-lg">Admissions {p.admission_open ? "are open" : "are closed"}</h3>
          <p className="mt-2 text-sm text-slate-600">Apply online in 4 simple steps — student details, parent OTP verification, document upload and review. You'll receive an application number on WhatsApp.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {p.admission_open && <Link to={`/s/${s.code}/admission`} className="pub-btn" data-testid="contact-apply-btn">Start Application <ArrowRight size={16} /></Link>}
            <Link to="/track" className="pub-btn pub-btn-outline">Track Application</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
